/* eslint-disable no-console */
import _throttle from 'lodash/throttle';
import { Lightning, Router } from '@lightningjs/sdk';
import merge from 'lodash/merge';
import { customerCode, staticFiles } from '@bedrock-rdk/config';
import { getInitialStyle, defaultContext, VIEWPORT_OFFSET_PERCENTAGE } from './staticPage.style';
import { removeSplashScreen } from '../../../../logic/app/app.utils';
import { filterNodes, parseFile } from '../../../tornado/staticFiles/staticFiles.utils';
import { renderFile } from '../../../tornado/staticFiles/staticFiles.renders';
import { Button } from '../../../tornado/button/button.component';
import Icon from '../../../tornado/icon/icon.component';
import { BrandIcon } from '../../../tornado/brand-icon/brandIcon.component';
import { t } from '../../../i18n';

const THROTTLE_MS = 100;

export class StaticPage extends Lightning.Component {
  static _template() {
    return merge(
      {
        Background: {},
        BackButton: {
          type: Button,
          label: t('global.back'),
          visible: false,
        },
        ArrowUp: {
          type: Icon,
        },
        BrandIcon: {
          type: BrandIcon,
        },
        ScrollWrapper: {
          GradientTop: {},
          ScrollContent: {},
          GradientBottom: {},
        },
        ArrowDown: {
          type: Icon,
        },
      },
      getInitialStyle(),
    );
  }

  _construct() {
    this._index = 0;
    this._containerH = 0;
    this._focusedContent = false;
    this._lastElementTxLoaded = false;

    this._throttleUp = _throttle(this._navigateUp, THROTTLE_MS);
    this._throttleDown = _throttle(this._navigateDown, THROTTLE_MS);
  }

  _active() {
    removeSplashScreen();
  }

  _inactive() {
    this._throttleUp.cancel();
    this._throttleDown.cancel();
  }

  set target(pageName) {
    const content = this.tag('ScrollContent');
    const fileURL = [staticFiles.base, customerCode, staticFiles.files[pageName]].join('/');
    parseFile(fileURL).then(
      data => {
        const textItems = renderFile(filterNodes(data), {
          ...this._config,
          wordWrap: this.tag('ScrollWrapper').w,
        });

        content.children = textItems;
        const lastElement = content.children[content.children.length - 1];
        if (lastElement) {
          lastElement.once('txLoaded', () => {
            this._lastElementTxLoaded = true;
          });
        }
      },
      // eslint-disable-next-line no-unused-vars
      err => {
        // TODO Error handling here
      },
    );

    this._updateFocus(true);
  }

  get viewPortHeight() {
    return Math.round(this.tag('ScrollWrapper').finalH * VIEWPORT_OFFSET_PERCENTAGE);
  }

  get _positions() {
    if (this._isSinglePage()) return 1;

    return Math.ceil(this.tag('ScrollContent').finalH / this.viewPortHeight);
  }

  _build() {
    this._config = defaultContext;
    const content = this.tag('ScrollContent');
    content.onUpdate = () => {
      const { finalH } = content;

      if (finalH > 0) {
        this.setPositions();
        this._updateFocus(!this._isSinglePage());
      }
    };
  }

  _updateFocus(value) {
    if (this._focusedContent !== value) {
      this._focusedContent = value;
      this._refocus();
    }
  }

  _isSinglePage() {
    const wrapperH = this.tag('ScrollWrapper').finalH;

    return wrapperH > this._containerH;
  }

  _getFocused() {
    if (this._focusedContent) {
      return undefined;
    }

    const button = this.tag('BackButton');
    if (button && button.visible) {
      return button;
    }

    return undefined;
  }

  setPositions() {
    const contentH = this.tag('ScrollContent').finalH;
    if (this._positions > 0 && this._containerH === contentH) {
      return;
    }
    this._containerH = contentH;
    this.setIndex();
  }

  setIndex(index = this._index) {
    this._index = index;

    const isLastPage = this.tag('ScrollContent').finalH - (this._index + 1) * this.viewPortHeight < this.viewPortHeight;
    let smooth = 0;
    if (isLastPage && this._lastElementTxLoaded) {
      smooth = this.tag('ScrollContent').finalH - this.viewPortHeight;
    } else {
      smooth = index ? this._index * this.viewPortHeight : 0;
    }

    this.patch({
      ArrowUp: {
        visible: this._index > 0,
      },
      BrandIcon: {
        visible: this._index === 0,
      },
      ScrollWrapper: {
        GradientTop: { visible: this._index > 0 },
        ScrollContent: {
          smooth: {
            y: -smooth,
          },
        },
      },
      ArrowDown: {
        visible: this._index < this._positions - 1 && !(isLastPage && this._lastElementTxLoaded),
      },
    });

    this.stage.textureManager.gc();
  }

  _navigateUp() {
    if (this._index > 0) {
      this.setIndex(this._index - 1);

      return true;
    }
    if (this._index === 0 && this._focusedContent) {
      this._updateFocus(false);

      return true;
    }

    return false;
  }

  _handleUp() {
    this._throttleUp();
  }

  _navigateDown() {
    if (!this._focusedContent && !this._isSinglePage()) {
      this._updateFocus(true);

      return true;
    }
    if (this._index < this._positions - 1 && !this._isSinglePage()) {
      this.setIndex(this._index + 1);

      return true;
    }

    return false;
  }

  _handleDown() {
    this._throttleDown();
  }

  _handleBack() {
    Router.back();

    return true;
  }

  _handleEnter() {
    if (this._getFocused() === this.tag('BackButton')) {
      Router.back();

      return true;
    }

    return false;
  }
}
